import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock, Loader2 } from "lucide-react";
import StateSelect from "./StateSelect";
import { submitApplication, isValidEmail, isValidPhone } from "../lib/apply";

export const fieldCls = "w-full h-12 rounded-lg bg-[#FAF7EE] border border-[#0F2A1F]/15 px-3.5 text-[15px] text-[#0F2A1F] placeholder:text-[#0F2A1F]/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow";

export default function QuickMatchForm({ cats = [] }) {
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", email: "", phone: "", state: "", category: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim()) return toast.error("What's your name?");
    if (!isValidEmail(f.email)) return toast.error("Please enter a valid email.");
    if (!isValidPhone(f.phone)) return toast.error("Please enter a valid phone number.");
    if (!f.state) return toast.error("Which state do you live in?");
    const label = cats.find((c) => c.key === f.category)?.label || "Any remote role";
    setBusy(true);
    try {
      const res = await submitApplication({ ...f, target_role: `Quick Match · ${label}`, note: "Submitted via homepage Quick Match" });
      nav("/thank-you", { state: { role: label, name: f.full_name, tier: res.priority_tier, appId: res.id } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <form id="quick-match" onSubmit={submit} noValidate className="scroll-mt-28 rounded-2xl bg-white text-[#0F2A1F] p-5 sm:p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] ring-1 ring-black/5" data-testid="quick-match-form">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-2xl sm:text-3xl font-bold leading-tight">Get matched — free</h2>
        <span className="shrink-0 rounded-full bg-[#10B981]/15 text-[#0d7a55] text-[11px] font-bold px-2.5 py-1">30 SEC</span>
      </div>
      <p className="mt-1 text-sm text-[#0F2A1F]/60">No account. No CV needed to start.</p>
      <div className="mt-5 space-y-3">
        <input className={fieldCls} placeholder="Full name" autoComplete="name" value={f.full_name} onChange={set("full_name")} data-testid="qm-name" />
        <input className={fieldCls} type="email" inputMode="email" placeholder="Email address" autoComplete="email" value={f.email} onChange={set("email")} data-testid="qm-email" />
        <input className={fieldCls} type="tel" inputMode="tel" placeholder="Phone number" autoComplete="tel" value={f.phone} onChange={set("phone")} data-testid="qm-phone" />
        <div className="grid sm:grid-cols-2 gap-3">
          <StateSelect value={f.state} onChange={set("state")} placeholder="Your state" className={fieldCls} testId="qm-state" />
          <select className={fieldCls} value={f.category} onChange={set("category")} data-testid="qm-category" aria-label="Field">
            <option value="">Any field</option>
            {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <button type="submit" disabled={busy} className="mt-5 w-full h-14 rounded-xl bg-[#D4AF37] text-[#0F2A1F] text-base font-bold inline-flex items-center justify-center gap-2 hover:bg-[#e2bf48] active:scale-[0.99] transition-[background-color,transform] disabled:opacity-70" data-testid="qm-submit">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Find my remote job <ArrowRight className="h-5 w-5" /></>}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[#0F2A1F]/55">
        <Lock className="h-3.5 w-3.5" /> 100% free for job seekers. We never sell your data.
      </p>
    </form>
  );
}
